'use client';

import * as React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  MessageSquare,
  Megaphone,
  Users,
  Hash,
  Send,
  Wallet,
  CornerDownLeft,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@mpange/ui';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { canAccessRoute } from '@/lib/permissions';

import { NAV_SECTIONS } from '@/lib/nav';

const NAV_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { accessToken, currentOrgId, organizations } = useAuth();
  const currentOrg = organizations.find((o) => o.id === currentOrgId);
  const [term, setTerm] = React.useState('');
  const [debouncedTerm, setDebouncedTerm] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(term.trim()), 120);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useQuery({
    queryKey: ['dashboard-search', currentOrgId, debouncedTerm],
    queryFn: () => api.searchDashboard(accessToken!, currentOrgId!, debouncedTerm),
    enabled: open && !!accessToken && !!currentOrgId && debouncedTerm.length > 0,
    staleTime: 30_000,
  });

  React.useEffect(() => {
    if (open) {
      setTerm('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onOpenChange]);

  const go = (href: string) => {
    onOpenChange(false);
    setTerm('');
    navigate(href);
  };

  const results = data?.data;
  const filteredNav = NAV_ITEMS.filter(
    (item) =>
      canAccessRoute(currentOrg?.role, item.href) &&
      item.label.toLowerCase().includes(term.trim().toLowerCase()),
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-[12vh]">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close command palette"
        onClick={() => onOpenChange(false)}
      />
      <div className="relative z-10 w-full max-w-[640px] overflow-hidden rounded-xl border border-border bg-surface shadow-modal animate-scale-in">
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search className="h-4 w-4 text-text-muted" />
          <input
            ref={inputRef}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search messages, campaigns, contacts, sender IDs…"
            className="h-12 w-full bg-transparent text-[14px] text-text outline-none placeholder:text-text-muted"
          />
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[11px] text-text-muted sm:block">
            ESC
          </kbd>
        </div>

        <div className="max-h-[420px] overflow-y-auto p-2">
          {term.trim().length === 0 && (
            <>
              <Group heading="Actions">
                <Item icon={Send} onSelect={() => go('/dashboard/sms/send')}>
                  Send SMS
                </Item>
                <Item icon={Megaphone} onSelect={() => go('/dashboard/campaigns/new')}>
                  Create campaign
                </Item>
                <Item icon={Wallet} onSelect={() => go('/dashboard/billing/topup')}>
                  Top up wallet
                </Item>
              </Group>
              <Group heading="Navigation">
                {NAV_ITEMS.map((item) => (
                  <Item key={item.href} icon={item.icon} onSelect={() => go(item.href)}>
                    {item.label}
                  </Item>
                ))}
              </Group>
            </>
          )}

          {term.trim().length > 0 && isFetching && (
            <p className="px-3 py-8 text-center text-[13px] text-text-muted">Searching…</p>
          )}

          {term.trim().length > 0 && !isFetching && debouncedTerm.length > 0 && results && (
            <>
              {results.messages.length === 0 &&
                results.campaigns.length === 0 &&
                results.contacts.length === 0 &&
                results.senderIds.length === 0 &&
                filteredNav.length === 0 && (
                  <p className="px-3 py-8 text-center text-[13px] text-text-muted">
                    No matches for “{term}”.
                  </p>
                )}

              {results.messages.length > 0 && (
                <Group heading="Messages">
                  {results.messages.map((msg) => (
                    <Item
                      key={msg.id}
                      icon={MessageSquare}
                      onSelect={() => go(`/dashboard/sms/${msg.id}`)}
                    >
                      {msg.destination}{' '}
                      <span className="text-text-muted">· {msg.sender} · {msg.status}</span>
                    </Item>
                  ))}
                </Group>
              )}

              {results.campaigns.length > 0 && (
                <Group heading="Campaigns">
                  {results.campaigns.map((c) => (
                    <Item
                      key={c.id}
                      icon={Megaphone}
                      onSelect={() => go(`/dashboard/campaigns/${c.id}`)}
                    >
                      {c.name} <span className="text-text-muted">· {c.status}</span>
                    </Item>
                  ))}
                </Group>
              )}

              {results.contacts.length > 0 && (
                <Group heading="Contacts">
                  {results.contacts.map((c) => (
                    <Item
                      key={c.id}
                      icon={Users}
                      onSelect={() => go(`/dashboard/contacts/${c.id}`)}
                    >
                      {[c.firstName, c.lastName].filter(Boolean).join(' ') || c.phoneE164}{' '}
                      <span className="text-text-muted">· {c.phoneE164}</span>
                    </Item>
                  ))}
                </Group>
              )}

              {results.senderIds.length > 0 && (
                <Group heading="Sender IDs">
                  {results.senderIds.map((s) => (
                    <Item
                      key={s.id}
                      icon={Hash}
                      onSelect={() => go('/dashboard/sender-ids')}
                    >
                      {s.value} <span className="text-text-muted">· {s.status}</span>
                    </Item>
                  ))}
                </Group>
              )}

              {filteredNav.length > 0 && (
                <Group heading="Navigation">
                  {filteredNav.map((item) => (
                    <Item key={item.href} icon={item.icon} onSelect={() => go(item.href)}>
                      {item.label}
                    </Item>
                  ))}
                </Group>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        {heading}
      </p>
      <div>{children}</div>
    </div>
  );
}

function Item({
  icon: Icon,
  children,
  onSelect,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13.5px] text-text hover:bg-brand-soft hover:text-brand',
      )}
    >
      <Icon className="h-4 w-4 text-text-muted" />
      <span className="min-w-0 truncate">{children}</span>
      <CornerDownLeft className="ml-auto h-3.5 w-3.5 shrink-0 text-text-muted opacity-0 group-hover:opacity-100" />
    </button>
  );
}
